import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  Avatar,
  Button,
  Drawer,
  Layout,
  Menu,
  Spin,
  Tooltip,
  Typography,
  theme,
} from "antd";
import {
  LoadingOutlined,
  MenuFoldOutlined,
  MenuOutlined,
  MenuUnfoldOutlined,
} from "@ant-design/icons";

import Logo from "../../assets/TRADEIQLOGO1.avif";
import { useAuth } from "components/auth/AuthContext";
// import NotificationBell from "components/notifications/NotificationBel";
import NotificationDropdown from "components/notifications/NotificationDrodown";
import { OrderProvider } from "services/OrderContext";
import { useUsersCampaigns } from "hooks/UseCampaigns";
import { usePWAInstall } from "components/features/InstallButton";
import {
  AdminSideBarMenu,
  MENU_ACTION_KEYS,
  ROLE_MENU_MAP,
  buildAntdMenuItems,
  getDefaultOpenKeys,
} from "./GroupedMenu";
import "../../styles/layout.css"

const { Sider, Header, Content } = Layout;
const { Text } = Typography;

// ---------------------------------------------------------------------------
// Helpers (outside component — stable references)
// ---------------------------------------------------------------------------

const getInitials = (name = "") =>
  name
    .split(" ")
    .map((n) => n[0]?.toUpperCase())
    .join("")
    .slice(0, 2) || "?";

const getMenuForRole = (role) => ROLE_MENU_MAP[role] ?? AdminSideBarMenu;

// Roles that do NOT require fulfillmentCenterId to render
const ROLES_WITHOUT_FC = new Set(["admin", "partner", "agentManager","rider","fulfillmentProvider"]);

// Dark theme background that matches Ant Design's default Sider dark colour
const SIDER_BG = "#001529";

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const AppLayout = () => {
  const { logout, getUserInfo } = useAuth();
  const { name, picture, role, id, fulfillmentCenterId, UserCode } = getUserInfo();
  const { canInstall, installApp } = usePWAInstall();
  const location = useLocation();
  const navigate = useNavigate();

  const {
    token: { colorBgContainer, colorBorderSecondary, colorPrimary },
  } = theme.useToken();

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [collapsed, setCollapsed] = useState(() => window.innerWidth <= 768);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    if (isMobile) setCollapsed(true);
  }, [isMobile]);

  const { data: userCampaignsData = [], isLoading: isUserCampaignsLoading } = useUsersCampaigns(id);

  const campaignIds = useMemo(
    () =>
      Array.from(
        new Set([
          ...(fulfillmentCenterId ? [fulfillmentCenterId] : []),
          ...userCampaignsData.map((item) => item.campaignId),
        ]),
      ),
    [fulfillmentCenterId, userCampaignsData],
  );

  const needsFC = role && !ROLES_WITHOUT_FC.has(role);
  const isDataReady = !!role && !!id && !isUserCampaignsLoading && (!needsFC || !!fulfillmentCenterId);

  const menuItems = useMemo(() => buildAntdMenuItems(getMenuForRole(role), canInstall), [role, canInstall]);
  const [defaultOpenKeys] = useState(() => getDefaultOpenKeys(getMenuForRole(role), location.pathname));

  const onMenuClick = useCallback(
    ({ key }) => {
      if (key === MENU_ACTION_KEYS.LOGOUT) {
        logout();
        return;
      }
      if (key === MENU_ACTION_KEYS.INSTALL) {
        installApp();
        return;
      }
      navigate(key);
      if (isMobile) setCollapsed(true);
    },
    [logout, installApp, navigate, isMobile],
  );

  const renderSidebarContent = (isCollapsedView = false) => (
    <>
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: isCollapsedView ? 48 : 72,
          padding: isCollapsedView ? "0 8px" : "0 16px",
          borderBottom: "1px solid rgba(255,255,255,0.08)",
          overflow: "hidden",
          flexShrink: 0,
        }}
      >
        <img
          src={Logo}
          alt="TradeIQ"
          style={{
            height: isCollapsedView ? 28 : 52,
            maxWidth: "100%",
            objectFit: "contain",
          }}
        />
      </div>

      <Menu
        theme="dark"
        mode="inline"
        selectedKeys={[location.pathname]}
        defaultOpenKeys={defaultOpenKeys}
        items={menuItems}
        onClick={onMenuClick}
        style={{
          borderRight: 0,
          overflowY: "auto",
          overflowX: "hidden",
          scrollbarWidth: "none",
        }}
      />
    </>
  );

  if (!isDataReady) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100vh" }}>
        <Spin indicator={<LoadingOutlined style={{ fontSize: 48, color: "#B19CD7" }} spin />} />
      </div>
    );
  }

  return (
    <OrderProvider role={role} userId={id} campaignId={campaignIds}>
      <Layout style={{ minHeight: "100vh", overflow: "hidden" }}>
        {isMobile ? (
          <Drawer
            placement="left"
            open={!collapsed}
            onClose={() => setCollapsed(true)}
            width={220}
            title={null}
            closable={false}
            styles={{
              body: {
                padding: 0,
                background: SIDER_BG,
                display: "flex",
                flexDirection: "column",
                height: "100%",
                overflow: "hidden",
              },
            }}
          >
            {renderSidebarContent(false)}
          </Drawer>
        ) : (
          <Sider
            trigger={null}
            collapsible
            collapsed={collapsed}
            collapsedWidth={80}
            width={220}
            style={{
              overflow: "auto",
              scrollbarWidth: "thin",
              height: "100vh",
              position: "sticky",
              top: 0,
              left: 0,
              display: "flex",
              flexDirection: "column",
              boxShadow: "2px 0 8px rgba(0,0,0,0.15)",
              background: SIDER_BG,
              zIndex: 101,
            }}
          >
            {renderSidebarContent(collapsed)}
          </Sider>
        )}

        {/* CRITICAL FIX HERE: 
          minWidth: 0 prevents flex child from expanding beyond viewport
          display: "flex", flexDirection: "column" frames the structural layers
        */}
        <Layout style={{ minWidth: 0, display: "flex", flexDirection: "column", height: "100vh" }}>
          <Header
            style={{
              padding: "0 24px 0 0",
              background: colorBgContainer,
              borderBottom: `1px solid ${colorBorderSecondary}`,
              display: "flex",
              alignItems: "center",
              position: "sticky",
              top: 0,
              zIndex: 100,
              height: 56,
              flexShrink: 0,
            }}
          >
            <Tooltip title={isMobile ? "Open menu" : collapsed ? "Expand" : "Collapse"} placement="right">
              <Button
                type="text"
                icon={isMobile ? <MenuOutlined /> : collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
                onClick={() => setCollapsed((prev) => !prev)}
                style={{
                  fontSize: 18,
                  width: 56,
                  height: 56,
                  flexShrink: 0,
                  color: "#595959",
                }}
              />
            </Tooltip>

            <div style={{ flex: 1 }} />

            <div style={{ display: "flex", alignItems: "center", gap: 24 }}>
              <Text type="secondary" style={{ fontSize: 13 }}>
                Code:&nbsp;<Text strong>{UserCode}</Text>
              </Text>

              {/* CLEANED NOTIFICATION WRAPPER */}
              <div style={{ display: "flex", alignItems: "center" }}>
                <NotificationDropdown />
              </div>

              {picture ? (
                <Avatar src={picture} size={34} style={{ flexShrink: 0 }} />
              ) : (
                <Avatar size={34} style={{ background: colorPrimary, fontWeight: 700, fontSize: 13, flexShrink: 0 }}>
                  {getInitials(name)}
                </Avatar>
              )}
            </div>
          </Header>

          {/* CRITICAL FIX HERE:
            overflowX: "auto" handles your wide Order data grid tables gracefully 
          */}
          <Content
            style={{
              background: "#f5f5f5",
              overflowY: "auto",
              overflowX: "scroll",
              flexGrow: 1,
              padding: "6px",
            }}
          >
            <div style={{ background: colorBgContainer, p: 24, borderRadius: 8, minHeight: "100%" }}>
              <Outlet />
            </div>
          </Content>
        </Layout>
      </Layout>
    </OrderProvider>
  );
};

export default AppLayout;
